// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { StyledBox } from "@steiner/XDL/Utilities";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    order?: OrderModel;
    extendedStyles?: OrderTotalDisplayStyles;
    // xng-changes - STR-2619
    children?: React.ReactNode;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    showPricing: state.context.showPricing,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface OrderTotalDisplayStyles {
    container?: GridContainerProps;
    subtotalLabelGridItem?: GridItemProps;
    subtotalLabel?: TypographyProps;
    subtotalValueGridItem?: GridItemProps;
    subtotalValue?: TypographyProps;
    discountsLabelGridItem?: GridItemProps;
    discountsLabel?: TypographyProps;
    discountsValueGridItem?: GridItemProps;
    discountsValue?: TypographyProps;
    shippingAndHandlingLabelGridItem?: GridItemProps;
    shippingAndHandlingLabel?: TypographyProps;
    shippingAndHandlingValueGridItem?: GridItemProps;
    shippingAndHandlingValue?: TypographyProps;
    otherChargesLabelGridItem?: GridItemProps;
    otherChargesLabel?: TypographyProps;
    otherChargesValueGridItem?: GridItemProps;
    otherChargesValue?: TypographyProps;
    taxLabelGridItem?: GridItemProps;
    taxLabel?: TypographyProps;
    taxValueGridItem?: GridItemProps;
    taxValue?: TypographyProps;
    totalLabelGridItem?: GridItemProps;
    totalLabel?: TypographyProps;
    totalValueGridItem?: GridItemProps;
    totalValue?: TypographyProps;
    // XNG-Changes
    fullWidthGridItem?: GridItemProps;
    styledHr?: InjectableCss;
    orderSummaryLabel?: TypographyProps;
    boxContainer?: InjectableCss;
}

export const orderTotalDisplayStyles: OrderTotalDisplayStyles = {
    container: {
        gap: 10,
        css: css`
            background-color: ${getColor("common.accent")};
            padding: 20px;
        `,
    },
    subtotalLabelGridItem: { width: 6 },
    subtotalValueGridItem: {
        css: css`
            @media print {
                text-align: right;
            }
        `,
        width: 6,
    },
    subtotalValue: {
        css: css`
            margin-left: auto;
        `,
    },
    discountsLabelGridItem: { width: 6 },
    discountsValueGridItem: {
        css: css`
            @media print {
                text-align: right;
            }
        `,
        width: 6,
    },
    discountsValue: {
        css: css`
            margin-left: auto;
        `,
    },
    shippingAndHandlingLabelGridItem: { width: 6 },
    shippingAndHandlingValueGridItem: {
        css: css`
            @media print {
                text-align: right;
            }
        `,
        width: 6,
    },
    shippingAndHandlingValue: {
        css: css`
            margin-left: auto;
        `,
    },
    otherChargesLabelGridItem: { width: 6 },
    otherChargesValueGridItem: {
        css: css`
            @media print {
                text-align: right;
            }
        `,
        width: 6,
    },
    otherChargesValue: {
        css: css`
            margin-left: auto;
        `,
    },
    taxLabelGridItem: { width: 6 },
    taxValueGridItem: {
        css: css`
            @media print {
                text-align: right;
            }
        `,
        width: 6,
    },
    taxValue: {
        css: css`
            margin-left: auto;
        `,
    },
    totalLabelGridItem: { width: 6 },
    totalLabel: { weight: "bold" },
    totalValueGridItem: {
        css: css`
            @media print {
                text-align: right;
            }
        `,
        width: 6,
    },
    totalValue: {
        weight: "bold",
        css: css`
            margin-left: auto;
        `,
    },
};

// XNG-Changes
const StyledHr = getStyledWrapper("hr");

const OrderTotalDisplay: FC<Props> = ({
    order,
    enableVat,
    showPricing,
    extendedStyles,
    // XNG-Changes
    children,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderTotalDisplayStyles, extendedStyles));

    if (!order || !showPricing) {
        return null;
    }

    // XNG-Changes
    const separator = (
        <GridItem {...styles.fullWidthGridItem} className="separator">
            <StyledHr {...styles.styledHr} />
        </GridItem>
    );

    // XNG-Changes
    return (
        <StyledBox {...styles.boxContainer}>
            <GridContainer {...styles.container}>
                <GridItem {...styles.fullWidthGridItem}>
                    <Typography {...styles.orderSummaryLabel}>{translate("Order Summary")}</Typography>
                </GridItem>
                {separator}
                <GridItem {...styles.subtotalLabelGridItem}>
                    <Typography {...styles.subtotalLabel}>
                        {translate("Subtotal") + (enableVat ? ` (${translate("Ex. VAT")})` : "")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.subtotalValueGridItem}>
                    <Typography {...styles.subtotalValue}>{order.orderSubTotalDisplay}</Typography>
                </GridItem>
                {order.discountAmount > 0 && (
                    <>
                        <GridItem {...styles.discountsLabelGridItem}>
                            <Typography {...styles.discountsLabel}>{translate("Discounts")}</Typography>
                        </GridItem>
                        <GridItem {...styles.discountsValueGridItem}>
                            <Typography
                                {...styles.discountsValue}
                                data-test-selector="discountAmount"
                            >{`-${order.orderDiscountAmountDisplay}`}</Typography>
                        </GridItem>
                    </>
                )}
                {order.shippingCharges + order.handlingCharges > 0 && (
                    <>
                        <GridItem {...styles.shippingAndHandlingLabelGridItem}>
                            <Typography {...styles.shippingAndHandlingLabel}>
                                {translate("Shipping & Handling")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.shippingAndHandlingValueGridItem}>
                            <Typography {...styles.shippingAndHandlingValue}>
                                {order.shippingAndHandlingDisplay}
                            </Typography>
                        </GridItem>
                    </>
                )}
                {order.otherCharges > 0 && (
                    <>
                        <GridItem {...styles.otherChargesLabelGridItem}>
                            <Typography {...styles.otherChargesLabel}>{translate("Other Charges")}</Typography>
                        </GridItem>
                        <GridItem {...styles.otherChargesValueGridItem}>
                            <Typography {...styles.otherChargesValue}>{order.otherChargesDisplay}</Typography>
                        </GridItem>
                    </>
                )}
                {(!order.orderHistoryTaxes || order.orderHistoryTaxes.length === 0) && (
                    <>
                        <GridItem {...styles.taxLabelGridItem}>
                            <Typography {...styles.taxLabel}>
                                {enableVat ? translate("VAT") : translate("Est. Tax")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.taxValueGridItem}>
                            <Typography {...styles.taxValue} data-test-selector="orderDetails_totalTaxDisplay">
                                {order.totalTaxDisplay}
                            </Typography>
                        </GridItem>
                    </>
                )}
                {order.orderHistoryTaxes &&
                    order.orderHistoryTaxes.map(tax => (
                        <>
                            <GridItem {...styles.taxLabelGridItem}>
                                <Typography>{tax.taxDescription || translate("Est. Tax")}</Typography>
                            </GridItem>
                            <GridItem {...styles.taxValueGridItem}>
                                <Typography {...styles.taxValue}>{tax.taxAmountDisplay}</Typography>
                            </GridItem>
                        </>
                    ))}
                {separator}
                <GridItem {...styles.totalLabelGridItem}>
                    <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                </GridItem>
                <GridItem {...styles.totalValueGridItem}>
                    <Typography {...styles.totalValue} data-test-selector="orderDetail_orderGrandTotalDisplay">
                        {order.orderGrandTotalDisplay}
                    </Typography>
                </GridItem>
                {children}
            </GridContainer>
        </StyledBox>
    );
};

export default connect(mapStateToProps)(OrderTotalDisplay);
